/**
 * NetobsPacketSparklineContent (NO-3) — inner content of the PACKETS debug tool.
 *
 * Renders 30s of packet traffic in two rows (▲ outbound, ▼ inbound) with
 * live bytes/sec + msgs/sec numerics + larger SVG sparkline per row, plus
 * a peak-bytes/s legend on the right.
 *
 * Substrate: useNetobsTraffic() (NetworkTracer.recentTraffic walker).
 * No new server emit.
 *
 * Visibility is no longer controlled inside this component — it always
 * renders when mounted. DebugHUDHost decides when to mount it (driven by
 * DebugToolsContext + DebugLauncher).
 */

import React from 'react';
import { NETOBS_COLORS, netobsHealthColor } from './colors';
import { useNetobsTraffic } from './useNetobsTraffic';
import { CopyButton } from '../debug/CopyButton';

const WINDOW_SECS = 30;

// Bytes/sec health thresholds. Tuned from observation; can adjust.
const BYTES_PER_SEC_BUDGET = 3000;  // "high" — anything above is a concerning sustained load

const SPARK_W = 220;
const SPARK_H = 40;

function fmtBytes(n: number): string {
  if (n < 1024) return `${n}B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)}K`;
  return `${(n / 1024 / 1024).toFixed(2)}M`;
}

function fmtBytesShort(n: number): string {
  if (n < 1024) return `${n}`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)}K`;
  return `${(n / 1024 / 1024).toFixed(1)}M`;
}

interface SparkRowProps {
  label: string;
  arrow: string;
  arrowColor: string;
  bytesPerSec: number;
  msgsPerSec: number;
  values: number[];
}

const SparkRow: React.FC<SparkRowProps> = ({ label, arrow, arrowColor, bytesPerSec, msgsPerSec, values }) => {
  const max = Math.max(1, ...values);
  const stepX = SPARK_W / Math.max(1, values.length - 1);
  const points = values
    .map((v, i) => `${(i * stepX).toFixed(1)},${(SPARK_H - (v / max) * SPARK_H).toFixed(1)}`)
    .join(' ');
  // Build a filled-area path: line + back along the bottom.
  const areaPath = values.length > 0
    ? `M0,${SPARK_H} L${points.split(' ').join(' L')} L${SPARK_W},${SPARK_H} Z`
    : '';
  const healthColor = netobsHealthColor(bytesPerSec, BYTES_PER_SEC_BUDGET);
  // Gridlines at 25%, 50%, 75% of max
  const gridYs = [0.25, 0.5, 0.75].map(p => SPARK_H * (1 - p));
  // Budget line (only shows if max exceeds budget — otherwise it'd hug the top)
  const budgetY = max > BYTES_PER_SEC_BUDGET
    ? SPARK_H - (BYTES_PER_SEC_BUDGET / max) * SPARK_H
    : null;
  // Spike markers: any bucket > 2× max-sustained-baseline (here approximated as median)
  const sortedVals = [...values].sort((a, b) => a - b);
  const baselineApprox = sortedVals[Math.floor(sortedVals.length / 2)] || 1;
  const spikeIndices = values
    .map((v, i) => v > Math.max(baselineApprox * 3, BYTES_PER_SEC_BUDGET) ? i : -1)
    .filter(i => i !== -1);

  return (
    <div style={{ marginTop: 4 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          whiteSpace: 'nowrap',
          fontSize: 12,
        }}
      >
        <span style={{ color: arrowColor, width: 14, textAlign: 'center', fontSize: 14 }}>{arrow}</span>
        <span style={{ width: 28, opacity: 0.7, fontWeight: 600 }}>{label}</span>
        <span style={{ color: healthColor, width: 64, fontWeight: 600 }}>{fmtBytes(bytesPerSec)}/s</span>
        <span style={{ opacity: 0.6, width: 56 }}>{msgsPerSec.toFixed(1)} m/s</span>
      </div>
      <div style={{ position: 'relative', marginTop: 2 }}>
        <svg width={SPARK_W} height={SPARK_H} style={{ display: 'block' }} aria-hidden="true">
          {/* Gridlines */}
          {gridYs.map((y, i) => (
            <line key={i} x1={0} y1={y} x2={SPARK_W} y2={y} stroke="#2a2f3a" strokeWidth={0.5} strokeDasharray="2,3" />
          ))}
          {/* Filled area under the curve */}
          {areaPath && (
            <path d={areaPath} fill={healthColor} fillOpacity={0.18} stroke="none" />
          )}
          {/* Budget threshold line — drawn only if exceeded */}
          {budgetY !== null && (
            <line
              x1={0} y1={budgetY} x2={SPARK_W} y2={budgetY}
              stroke="#ff5f5f" strokeWidth={0.8} strokeDasharray="4,2"
              opacity={0.55}
            />
          )}
          {/* Sparkline polyline */}
          <polyline
            fill="none"
            stroke={healthColor}
            strokeWidth={1.5}
            points={points}
          />
          {/* Spike markers — vertical red ticks */}
          {spikeIndices.map(i => {
            const x = i * stepX;
            return (
              <line key={i} x1={x} y1={0} x2={x} y2={SPARK_H}
                stroke="#ff5f5f" strokeWidth={0.8} opacity={0.4}
              />
            );
          })}
        </svg>
        <span style={{
          position: 'absolute',
          top: -1,
          right: 2,
          fontSize: 9,
          color: '#666',
          background: 'rgba(8,8,15,0.7)',
          padding: '0 3px',
          borderRadius: 2,
        }}>
          peak {fmtBytesShort(max)}/s
        </span>
      </div>
    </div>
  );
};

export const NetobsPacketSparklineContent: React.FC = () => {
  const snap = useNetobsTraffic(WINDOW_SECS);

  const inSeries = snap.buckets.map((b) => b.bytesIn);
  const outSeries = snap.buckets.map((b) => b.bytesOut);

  return (
    <div
      data-testid="netobs-packet-sparkline"
      style={{
        color: '#ddd',
        fontFamily: 'monospace',
        fontSize: 12,
        lineHeight: 1.35,
        padding: '4px 6px',
        userSelect: 'none',
      }}
    >
      <div style={{
        color: '#7af',
        marginBottom: 2,
        letterSpacing: '0.08em',
        fontSize: 11,
        fontWeight: 600,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <span>PACKETS</span>
        <span style={{ color: '#666', fontWeight: 400 }}>last {WINDOW_SECS}s</span>
        <CopyButton getText={() => [
          `PACKET SPARKLINE @ ${new Date().toISOString()} (window=${WINDOW_SECS}s)`,
          `↑ OUT: ${snap.bytesOutPerSec.toFixed(0)}B/s · ${snap.msgsOutPerSec.toFixed(1)}msgs/s`,
          `↓ IN:  ${snap.bytesInPerSec.toFixed(0)}B/s · ${snap.msgsInPerSec.toFixed(1)}msgs/s`,
          `buckets: ${snap.buckets.length}`,
          `out series: [${outSeries.join(', ')}]`,
          `in series:  [${inSeries.join(', ')}]`,
        ].join('\n')} />
      </div>
      <SparkRow
        label="OUT"
        arrow="▲"
        arrowColor={NETOBS_COLORS.DIR_OUT}
        bytesPerSec={snap.bytesOutPerSec}
        msgsPerSec={snap.msgsOutPerSec}
        values={outSeries}
      />
      <SparkRow
        label="IN"
        arrow="▼"
        arrowColor={NETOBS_COLORS.DIR_IN}
        bytesPerSec={snap.bytesInPerSec}
        msgsPerSec={snap.msgsInPerSec}
        values={inSeries}
      />
      <div style={{
        marginTop: 4,
        paddingTop: 4,
        borderTop: '1px solid #1f2330',
        fontSize: 10,
        color: '#888',
        display: 'flex',
        justifyContent: 'space-between',
      }}>
        <span>tot ↓ {fmtBytes(snap.totalBytesIn)} / ↑ {fmtBytes(snap.totalBytesOut)}</span>
        <span>{snap.totalMsgsIn + snap.totalMsgsOut} msgs</span>
      </div>
      {!snap.ready && (
        <div style={{ marginTop: 3, opacity: 0.55, fontSize: 10 }}>
          waiting for tracer…
        </div>
      )}
    </div>
  );
};
